<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  value: { type: Number, required: true },
  min:   { type: Number, default: 0 },
  max:   { type: Number, default: 100 },
  unit:  { type: String, default: '%' },

  // 게이지 각도
  startAngle: { type: Number, default: -90 },
  endAngle:   { type: Number, default: 90 },

  // 색상/스타일
  color:         { type: String, default: '#10b981' },
  trackColor:    { type: String, default: '#e5e7eb' },
  pointerColor:  { type: String, default: '#ef4444' },
  height:        { type: [Number, String], default: 250 },
  hollowSize:    { type: String, default: '58%' },

  // 포인터 반지름 비율(차트 반지름 대비)
  pointerRadiusScale: { type: Number, default: 0.55 },

  // ===== 타이틀 옵션 =====
  title:       { type: String, default: '' },
  subtitle:    { type: String, default: '' },
  titleAlign:  { type: String as () => 'left' | 'center' | 'right', default: 'center' },
})

const pct = computed(() => {
  const v = Math.min(Math.max(props.value, props.min), props.max)
  if (props.max === props.min) return 0
  return (v - props.min) / (props.max - props.min)
})

const displayValue = computed(() => {
  const v = Math.min(Math.max(props.value, props.min), props.max)
  return `${v}${props.unit}`
})

const series = ref([Math.round(pct.value * 1000) / 10])
watch(pct, p => { series.value = [Math.round(p * 1000) / 10] })

const angleDeg = computed(() =>
  props.startAngle + pct.value * (props.endAngle - props.startAngle)
)

const options = ref<any>({
  chart: {
    type: 'radialBar',
    sparkline: { enabled: true },
    toolbar: { show: false },
    animations: { enabled: true, dynamicAnimation: { speed: 500 } },
  },
  plotOptions: {
    radialBar: {
      startAngle: props.startAngle,
      endAngle: props.endAngle,
      hollow: { size: props.hollowSize },
      track: { background: props.trackColor, strokeWidth: '100%' },
      dataLabels: { show: false },
    },
  },
  fill: { colors: [props.color] },
  stroke: { lineCap: 'round' },
})

/** 포인터 반지름 계산은 '차트 박스' 크기 기준으로만 수행 */
const chartBoxRef = ref<HTMLElement | null>(null)
const radiusPx = ref(0)
let ro: ResizeObserver | null = null

const updateRadius = () => {
  const el = chartBoxRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const minDim = Math.min(rect.width, rect.height)
  radiusPx.value = (minDim / 2) * props.pointerRadiusScale
}

onMounted(() => {
  updateRadius()
  ro = new ResizeObserver(() => updateRadius())
  if (chartBoxRef.value) ro.observe(chartBoxRef.value)
})
onBeforeUnmount(() => { if (ro) ro.disconnect() })

const hasHeader = computed(() => !!(props.title || props.subtitle))
</script>

<template>
  <div class="gauge-wrap common-card col-12 h-100">
    <!-- ===== 상단 타이틀 영역 ===== -->
    <div
      v-if="hasHeader"
      class="gauge-header"
      :style="{ textAlign: titleAlign }"
    >
      <div v-if="title" class="title">{{ title }}</div>
      <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
      <!-- 필요 시 슬롯으로 완전 커스텀도 가능 -->
      <slot name="header" />
    </div>

    <!-- ===== 차트 영역(절대좌표 오버레이 기준 컨테이너) ===== -->
    <div
      class="chart-box"
      ref="chartBoxRef"
      :style="{
        '--needle-angle': angleDeg + 'deg',
        '--needle-color': pointerColor,
        '--needle-radius': radiusPx + 'px',
        '--chart-height': (typeof height === 'number' ? height + 'px' : height)
      }"
    >
      <ClientOnly>
        <apexchart
          type="radialBar"
          :options="options"
          :series="series"
          :height="height"
        />
      </ClientOnly>

      <!-- 중앙 값 라벨 -->
      <div class="center-label">
        <div class="value">{{ displayValue }}</div>
      </div>

      <!-- 포인터 -->
      <div class="needle">
        <!-- <div class="stem" />  필요 시 선 추가 -->
        <div class="tip" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.gauge-wrap {
  display: inline-block;
  width: 100%;
}

/* ===== Header ===== */
.gauge-header {
  margin-bottom: 10px;
}
.gauge-header .title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}
.gauge-header .subtitle {
  margin-top: 2px;
  font-size: 13px;
  color: #6b7280; /* gray-500 */
}

/* ===== Chart Box ===== */
.chart-box {
  position: relative;
  height: var(--chart-height, 260px);
}

/* 중앙값 라벨 */
.center-label {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  pointer-events: none;
}
.center-label .value {
  font-size: 28px; font-weight: 700; line-height: 1.1;
}

/* 포인터 */
.needle {
  position: absolute; left: 50%; top: 50%;
  width: 0; height: 0;
  transform: rotate(var(--needle-angle, 0deg));
  transform-origin: 50% 50%;
  pointer-events: none;
}

/* 선택: 선(스템) */
.stem {
  position: absolute; left: 50%; top: 50%;
  width: 2px; height: calc(var(--needle-radius) - 10px);
  background: var(--needle-color, #ef4444);
  transform: translate(-50%, calc(-1 * var(--needle-radius)));
  opacity: .8;
}

/* 포인터 끝 삼각형 */
.tip {
  position: absolute; left: 50%; top: 50%;
  width: 0; height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 14px solid var(--needle-color, #ef4444);
  transform: translate(-50%, calc(-1 * var(--needle-radius)));
  filter: drop-shadow(0 1px 2px rgba(0,0,0,.25));
}
</style>
